import type { Reason } from './reasons';

/**
 * The closed vocabulary every report is filed under.
 *
 * A report carries exactly one domain and one reason, and both leave as tags.
 * That pair is what makes the dashboard searchable: `domain:checkout` finds
 * every checkout failure without knowing which file raised it, and adding
 * `reason:` narrows it to one kind — something a free-text message prefix
 * could never do, because a prefix is only ever matched as a substring of a
 * title that also carries ids, URLs and status codes.
 */
export const DOMAIN = {
  address: 'address',
  analytics: 'analytics',
  api: 'api',
  auth: 'auth',
  cart: 'cart',
  checkout: 'checkout',
  chunk: 'chunk',
  eta: 'eta',
  geo: 'geo',
  hydration: 'hydration',
  payment: 'payment',
  rn: 'rn',
  ssr: 'ssr',
  ui: 'ui',
} as const;

export type Domain = (typeof DOMAIN)[keyof typeof DOMAIN];

/**
 * Where the code is running, as a tag value.
 *
 * Reported as `surface` rather than `platform` because Sentry already defines
 * `platform` as an event field (`javascript`, `node`, …); a tag of the same
 * name competes with it in search and cannot be relied on.
 */
export type Surface = 'web' | 'rn-ios' | 'rn-android' | 'rn-unknown' | 'ssr';

/**
 * Context shared by every event, supplied by the app rather than read from it.
 *
 * The telemetry module imports no store: a store that reports would otherwise
 * close a cycle back through the module it reports to. The app registers a
 * provider instead, and `beforeSend` pulls from it as each event leaves — so
 * every event is stamped, including the uncaught ones the SDK captures on its
 * own, and a value that arrives late (the native version comes with the React
 * Native handshake) still reaches reports made before it.
 */
export interface GlobalContext {
  company: string;
  surface: Surface;
  /** Native app version. Absent in the browser, and until the RN handshake lands. */
  appVersion?: string;
  /**
   * Native version plus build number plus CodePush revision.
   *
   * Kept alongside `appVersion` because the two answer different questions:
   * `app_version` is what you filter a range on, `app_build` is what you quote
   * when asking mobile which exact artefact a customer is running.
   */
  appBuild?: string;
}

export interface ReportOptions {
  /**
   * Why this was reported, as a stable slug from `REASON`.
   *
   * It becomes the `reason` tag and part of the fingerprint, so it must not
   * carry ids, URLs or counts — anything variable belongs in `extras`.
   */
  reason: Reason;
  /** Structured payload. Capped in depth and size before it leaves. */
  extras?: Record<string, unknown>;
  /**
   * Reports at most once per page session for this domain and reason.
   *
   * For a condition that does not change within a session, every repeat is a
   * duplicate that costs quota and inflates the event count past the number of
   * users actually affected.
   */
  once?: boolean;
  /**
   * Extra fingerprint parts, for a reason that legitimately splits.
   *
   * Use a bounded value — a status code, a payment system. A request id here
   * would file every occurrence as its own issue.
   */
  fingerprint?: string[];
}

/**
 * `reportError` only. Kept off `ReportOptions` so that passing a level to
 * `reportWarning` does not type-check: an option a function silently ignores
 * is the same class of trap this module exists to remove.
 *
 * For an exception that is expected operationally rather than broken — a chunk
 * request that lost a race with a deploy — `error` overstates it and pulls the
 * error rate along with it.
 */
export interface ReportErrorOptions extends ReportOptions {
  level?: 'warning' | 'error';
}
